import React from "react";
import styled from "styled-components";
import {
  INCIDENT_REPORT_FIELD_ORDER,
  JsonSchema,
} from "../schema/incidentReportSchema";
import {
  datetimeLocalInputToIso,
  isoToDatetimeLocalInput,
  ISSUE_TEMPLATE_SELECT_FIELD_IDS,
} from "../utils/incidentReportKeys";

const ISSUE_TEMPLATE_SELECT_FIELD_ID_SET = new Set<string>(
  ISSUE_TEMPLATE_SELECT_FIELD_IDS
);

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  background: ${({ theme }) => theme.colors.surface};
  transition: border-color ${({ theme }) => theme.transitions.base},
    box-shadow ${({ theme }) => theme.transitions.base};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }

  &[readonly] {
    background: ${({ theme }) => theme.colors.surfaceSecondary};
    cursor: default;
    border-color: ${({ theme }) => theme.colors.borderLight};
  }
`;

const ArrayRow = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const SmallButton = styled.button`
  flex-shrink: 0;
  padding: 0.35rem 0.65rem;
  font-size: 0.75rem;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surfaceSecondary};
  color: ${({ theme }) => theme.colors.textPrimary};
  cursor: pointer;
  &:hover {
    background: ${({ theme }) => theme.colors.borderLight};
  }
`;

const AssigneeSelect = styled.select`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  background: ${({ theme }) => theme.colors.surface};
  transition: border-color ${({ theme }) => theme.transitions.base},
    box-shadow ${({ theme }) => theme.transitions.base};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.surfaceSecondary};
    cursor: wait;
    border-color: ${({ theme }) => theme.colors.borderLight};
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 0.75rem 1rem;
  border: 2px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  background: ${({ theme }) => theme.colors.surface};
  font-family: ${({ theme }) => theme.fonts.base};
  resize: vertical;
  transition: border-color ${({ theme }) => theme.transitions.base},
    box-shadow ${({ theme }) => theme.transitions.base};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }

  &[readonly] {
    background: ${({ theme }) => theme.colors.surfaceSecondary};
    cursor: default;
    border-color: ${({ theme }) => theme.colors.borderLight};
  }
`;

export type AssigneeSelectOption = { value: string; label: string };
export type MasterSelectOption = AssigneeSelectOption;

type Props = {
  schema: JsonSchema;
  data: Record<string, any> | null | undefined;
  /** true のとき項目を編集可能にする（errors は常に参照のみ） */
  editable?: boolean;
  onFieldChange?: (key: string, value: unknown) => void;
  /** 担当者（issue_person_in_charge）をプルダウンで選ぶときの候補（value=ユーザーUUID） */
  assigneeSelectOptions?: AssigneeSelectOption[];
  /** 担当者一覧取得中 */
  assigneesSelectLoading?: boolean;
  /** テスト実行環境マスタ（value=表示名。課題作成時に ID へ解決） */
  executionEnvironmentSelectOptions?: MasterSelectOption[];
  executionEnvironmentsLoading?: boolean;
  /** ビルドバージョンマスタ（value=表示名） */
  buildVersionSelectOptions?: MasterSelectOption[];
  buildVersionsLoading?: boolean;
  /** 課題テンプレートのマスタ選択肢（key=フィールドID, value=UUID, label=表示名） */
  templateFieldSelectOptions?: Record<string, MasterSelectOption[]>;
  templateFieldsLoading?: boolean;
};

const isNonEmptyArray = (v: unknown) => Array.isArray(v) && v.length > 0;

function parseEditableArrayField(raw: string, itemType: string | undefined): unknown {
  const t = raw.trim();
  if (!t) return [];
  try {
    const p = JSON.parse(t);
    if (Array.isArray(p)) return p;
  } catch {
    /* use line split */
  }
  if (itemType === "string") {
    return t
      .split(/\n|,/)
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
}

function coerceScalarToStringArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((x) => (x === null || x === undefined ? "" : String(x)));
  }
  if (typeof value === "string") {
    const t = value.trim();
    if (!t) return [];
    try {
      const p = JSON.parse(t);
      if (Array.isArray(p)) return p.map((x) => String(x));
    } catch {
      /* 単一の日時文字列など */
    }
    return [t];
  }
  if (value !== undefined && value !== null) return [String(value)];
  return [];
}

function arrayItemsArePrimitiveStrings(def: { items?: unknown }): boolean {
  const it = def?.items;
  if (!it || typeof it !== "object") return false;
  if (Array.isArray(it)) {
    const first = it[0];
    return !!(
      first &&
      typeof first === "object" &&
      (first as { type?: string }).type === "string"
    );
  }
  const o = it as { type?: string; format?: string };
  return (
    o.type === "string" ||
    o.type === "number" ||
    typeof o.format === "string"
  );
}

function buildSelectOptionsWithCurrent(
  options: MasterSelectOption[],
  current: string
): MasterSelectOption[] {
  if (!current || options.some((o) => o.value === current)) return options;
  return [{ value: current, label: `${current}（一覧外）` }, ...options];
}

export const SchemaRenderer: React.FC<Props> = ({
  schema,
  data,
  editable = false,
  onFieldChange,
  assigneeSelectOptions = [],
  assigneesSelectLoading = false,
  executionEnvironmentSelectOptions = [],
  executionEnvironmentsLoading = false,
  buildVersionSelectOptions = [],
  buildVersionsLoading = false,
  templateFieldSelectOptions = {},
  templateFieldsLoading = false,
}) => {
  if (!schema || !data) return null;

  const order = INCIDENT_REPORT_FIELD_ORDER;
  const keys = Object.keys(schema.properties);
  const sortedKeys = order
    .filter((k) => keys.includes(k))
    .concat(keys.filter((k) => !order.includes(k)));

  const renderValue = (key: string, def: any, value: any) => {
    const title = def?.title || key;
    const type = def?.type;

    if (type === "string") {
      const strVal =
        typeof value === "string"
          ? value
          : value === undefined || value === null
            ? ""
            : String(value);
      const isAssigneeField = key === "issue_person_in_charge";
      const templateOptions = templateFieldSelectOptions[key];
      const hasTemplateSelect =
        Array.isArray(templateOptions) && templateOptions.length > 0;

      if (
        !isAssigneeField &&
        !hasTemplateSelect &&
        !editable &&
        (typeof value !== "string" || value.trim() === "")
      )
        return null;

      const renderTemplateStringSelect = () => {
        const opts = templateOptions ?? [];
        const optHit = opts.find((o) => o.value === strVal);
        const displayLabel = optHit?.label ?? strVal;
        if (!editable) {
          if (!strVal && !templateFieldsLoading) return null;
          return (
            <FormGroup key={key}>
              <Label htmlFor={`schema-${key}`}>{title}</Label>
              <Input
                id={`schema-${key}`}
                type="text"
                value={templateFieldsLoading ? "読み込み中…" : displayLabel}
                readOnly
              />
            </FormGroup>
          );
        }
        const selectValue =
          strVal && opts.some((o) => o.value === strVal) ? strVal : "";
        const noOptions = !templateFieldsLoading && opts.length === 0;
        return (
          <FormGroup key={key}>
            <Label htmlFor={`schema-${key}`}>{title}</Label>
            <AssigneeSelect
              id={`schema-${key}`}
              disabled={templateFieldsLoading || noOptions || !onFieldChange}
              value={selectValue}
              onChange={(e) => {
                const v = e.target.value;
                if (!onFieldChange) return;
                onFieldChange(key, v || "");
              }}
            >
              <option value="">
                {templateFieldsLoading
                  ? "選択肢を読み込み中…"
                  : noOptions
                    ? "選択肢を取得できませんでした"
                    : "選択してください"}
              </option>
              {buildSelectOptionsWithCurrent(opts, selectValue || strVal).map(
                (o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                )
              )}
            </AssigneeSelect>
          </FormGroup>
        );
      };

      if (
        hasTemplateSelect ||
        (editable &&
          templateFieldsLoading &&
          ISSUE_TEMPLATE_SELECT_FIELD_ID_SET.has(key))
      ) {
        return renderTemplateStringSelect();
      }

      if (
        isAssigneeField &&
        (editable ||
          assigneeSelectOptions.length > 0 ||
          assigneesSelectLoading)
      ) {
        const optHit = assigneeSelectOptions.find((o) => o.value === strVal);
        const displayLabel = optHit?.label ?? strVal;
        if (!editable) {
          if (!strVal && !assigneesSelectLoading) return null;
          return (
            <FormGroup key={key}>
              <Label htmlFor={`schema-${key}`}>{title}</Label>
              <Input
                id={`schema-${key}`}
                type="text"
                value={assigneesSelectLoading ? "読み込み中…" : displayLabel}
                readOnly
              />
            </FormGroup>
          );
        }
        const selectValue =
          strVal && assigneeSelectOptions.some((o) => o.value === strVal)
            ? strVal
            : "";
        const noAssigneesLoaded =
          !assigneesSelectLoading && assigneeSelectOptions.length === 0;
        return (
          <FormGroup key={key}>
            <Label htmlFor={`schema-${key}`}>{title}</Label>
            <AssigneeSelect
              id={`schema-${key}`}
              disabled={
                assigneesSelectLoading || noAssigneesLoaded || !onFieldChange
              }
              value={selectValue}
              onChange={(e) => {
                const v = e.target.value;
                if (!onFieldChange) return;
                if (!v) {
                  onFieldChange(key, "");
                  return;
                }
                onFieldChange(key, v);
              }}
            >
              <option value="">
                {assigneesSelectLoading
                  ? "担当者一覧を読み込み中…"
                  : noAssigneesLoaded
                    ? "担当者一覧を取得できませんでした"
                    : "選択してください"}
              </option>
              {assigneeSelectOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </AssigneeSelect>
          </FormGroup>
        );
      }

      const isLong = strVal.length > 80 || strVal.includes("\n");
      return (
        <FormGroup key={key}>
          <Label htmlFor={`schema-${key}`}>{title}</Label>
          {isLong ? (
            <Textarea
              id={`schema-${key}`}
              value={strVal}
              readOnly={!editable}
              onChange={
                editable && onFieldChange
                  ? (e) => onFieldChange(key, e.target.value)
                  : undefined
              }
            />
          ) : (
            <Input
              id={`schema-${key}`}
              type="text"
              value={strVal}
              readOnly={!editable}
              onChange={
                editable && onFieldChange
                  ? (e) => onFieldChange(key, e.target.value)
                  : undefined
              }
            />
          )}
        </FormGroup>
      );
    }

    if (type === "array") {
      const primitiveStrings = arrayItemsArePrimitiveStrings(def);
      const arr: unknown[] = primitiveStrings
        ? coerceScalarToStringArray(value)
        : Array.isArray(value)
          ? value
          : [];
      if (!editable && !isNonEmptyArray(arr)) return null;
      const first = arr[0];
      const isObjectElement =
        first !== null &&
        first !== undefined &&
        typeof first === "object" &&
        !Array.isArray(first);

      const itemFormat =
        def?.items &&
        typeof def.items === "object" &&
        !Array.isArray(def.items)
          ? ((def.items as { format?: string }).format as string | undefined)
          : undefined;
      const isDateTimeArray = itemFormat === "date-time";

      const renderMasterArraySelect = (
        fieldKey: string,
        options: MasterSelectOption[],
        loading: boolean
      ) => {
        const current = arr.length > 0 ? String(arr[0] ?? "").trim() : "";
        const selectOptions = buildSelectOptionsWithCurrent(options, current);
        const noMasters = !loading && options.length === 0;
        if (!editable && !current && !loading) return null;
        if (!editable) {
          const label =
            selectOptions.find((o) => o.value === current)?.label ?? current;
          return (
            <FormGroup key={fieldKey}>
              <Label htmlFor={`schema-${fieldKey}`}>{title}</Label>
              <Input
                id={`schema-${fieldKey}`}
                type="text"
                value={loading ? "読み込み中…" : label}
                readOnly
              />
            </FormGroup>
          );
        }
        return (
          <FormGroup key={fieldKey}>
            <Label htmlFor={`schema-${fieldKey}`}>{title}</Label>
            <AssigneeSelect
              id={`schema-${fieldKey}`}
              disabled={loading || noMasters || !onFieldChange}
              value={
                current && selectOptions.some((o) => o.value === current)
                  ? current
                  : ""
              }
              onChange={(e) => {
                const v = e.target.value;
                if (!onFieldChange) return;
                onFieldChange(fieldKey, v ? [v] : []);
              }}
            >
              <option value="">
                {loading
                  ? "一覧を読み込み中…"
                  : noMasters
                    ? "マスタを取得できませんでした"
                    : "選択してください"}
              </option>
              {selectOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </AssigneeSelect>
          </FormGroup>
        );
      };

      if (
        key === "issue_test_execution_environment" &&
        (editable ||
          executionEnvironmentSelectOptions.length > 0 ||
          executionEnvironmentsLoading)
      ) {
        return renderMasterArraySelect(
          key,
          executionEnvironmentSelectOptions,
          executionEnvironmentsLoading
        );
      }

      if (
        key === "issue_build_version" &&
        (editable ||
          buildVersionSelectOptions.length > 0 ||
          buildVersionsLoading)
      ) {
        return renderMasterArraySelect(
          key,
          buildVersionSelectOptions,
          buildVersionsLoading
        );
      }

      if (isDateTimeArray && (editable || isNonEmptyArray(arr))) {
        const isoRaw = arr.length > 0 ? String(arr[0] ?? "").trim() : "";
        const localVal = isoRaw ? isoToDatetimeLocalInput(isoRaw) : "";
        if (!editable && !isoRaw) return null;
        return (
          <FormGroup key={key}>
            <Label htmlFor={`schema-${key}`}>{title}</Label>
            <Input
              id={`schema-${key}`}
              type="datetime-local"
              value={localVal}
              readOnly={!editable}
              onChange={
                editable && onFieldChange
                  ? (e) => {
                      const iso = datetimeLocalInputToIso(e.target.value);
                      onFieldChange(key, iso ? [iso] : []);
                    }
                  : undefined
              }
            />
          </FormGroup>
        );
      }

      if (
        editable &&
        onFieldChange &&
        primitiveStrings &&
        !isObjectElement
      ) {
        const rows =
          arr.length > 0
            ? arr.map((x) =>
                x === null || x === undefined ? "" : String(x)
              )
            : [""];
        const commitRows = (next: string[]) => {
          const hasText = next.some((s) => s.trim().length > 0);
          onFieldChange(key, hasText ? next : []);
        };
        return (
          <FormGroup key={key}>
            <Label>{title}</Label>
            {rows.map((row, i) => (
              <ArrayRow key={`${key}-${i}`}>
                <Input
                  id={`schema-${key}-${i}`}
                  type="text"
                  value={row}
                  onChange={(e) => {
                    const next = [...rows];
                    next[i] = e.target.value;
                    commitRows(next);
                  }}
                />
                <SmallButton
                  type="button"
                  onClick={() => {
                    const next = rows.filter((_, j) => j !== i);
                    commitRows(next.length ? next : [""]);
                  }}
                >
                  削除
                </SmallButton>
              </ArrayRow>
            ))}
            <SmallButton
              type="button"
              onClick={() => commitRows([...rows, ""])}
            >
              行を追加
            </SmallButton>
          </FormGroup>
        );
      }

      const joined = isObjectElement
        ? JSON.stringify(arr, null, 2)
        : arr.length > 0
          ? arr.join(def?.items?.type === "string" ? "\n" : ", ")
          : editable
            ? "[]"
            : "";
      if (!editable && !joined) return null;
      return (
        <FormGroup key={key}>
          <Label htmlFor={`schema-${key}`}>{title}</Label>
          <Textarea
            id={`schema-${key}`}
            value={joined}
            readOnly={!editable}
            onChange={
              editable && onFieldChange
                ? (e) =>
                    onFieldChange(
                      key,
                      parseEditableArrayField(
                        e.target.value,
                        def?.items?.type
                      )
                    )
                : undefined
            }
          />
        </FormGroup>
      );
    }

    if (type === "object" && key === "errors") {
      const errorSummary = value?.error_summary;
      const details = value?.error_details;
      if (!errorSummary && !isNonEmptyArray(details)) return null;
      return (
        <FormGroup key={key}>
          <Label>エラー情報</Label>
          {errorSummary && (
            <FormGroup>
              <Label>エラー全体の概要</Label>
              <Textarea value={errorSummary} readOnly />
            </FormGroup>
          )}
          {isNonEmptyArray(details) && (
            <FormGroup>
              <Label>エラー詳細</Label>
              <Textarea
                value={details
                  .map(
                    (d: any) =>
                      `項目: ${d.field_name} / 理由: ${d.error_reason}`
                  )
                  .join("\n")}
                readOnly
              />
            </FormGroup>
          )}
        </FormGroup>
      );
    }

    // Fallback: render JSON string for unknown structures
    if (value !== undefined && value !== null) {
      const text =
        typeof value === "string" ? value : JSON.stringify(value, null, 2);
      if (!text && !editable) return null;
      return (
        <FormGroup key={key}>
          <Label htmlFor={`schema-${key}`}>{title}</Label>
          <Textarea
            id={`schema-${key}`}
            value={text || ""}
            readOnly={!editable}
            onChange={
              editable && onFieldChange
                ? (e) => {
                    const v = e.target.value.trim();
                    if (!v) {
                      onFieldChange(key, undefined);
                      return;
                    }
                    try {
                      onFieldChange(key, JSON.parse(v));
                    } catch {
                      onFieldChange(key, e.target.value);
                    }
                  }
                : undefined
            }
          />
        </FormGroup>
      );
    }
    return null;
  };

  return (
    <div>
      {sortedKeys.map((key) =>
        renderValue(key, schema.properties[key], (data as any)[key])
      )}
    </div>
  );
};

export default SchemaRenderer;
